import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ChevronDown, FileText, Folder, FolderOpen, Image, Inbox, LucideIcon, Users, Building2 } from 'lucide-react';
import { stakeholderService } from '../../services/stakeholderService';
import { organizationService } from '../../services/organizationService';
import { ENTITY_TONE, SearchInput, Skeleton } from '../ui';

import { foldText } from '../../utils/fuzzySearch';
export interface TreeSelection {
  type: 'all' | 'stakeholder' | 'organization' | 'stakeholders' | 'organizations' | 'unassigned';
  id?: string;
  name?: string;
}

interface EntityNode {
  id: string;
  name: string;
  count: number;
}

interface EntityTreeSidebarProps {
  mode: 'wiki' | 'media';
  selection: TreeSelection;
  onSelect: (sel: TreeSelection) => void;
  /** Map of entityType:entityId -> count for badges */
  entityCounts?: Record<string, number>;
  totalCount?: number;
  /**
   * `bare` (default): no surface of its own, compact rows; fits inside a card or panel.
   * `column`: grey secondary column with a hairline right edge, like the app sidebar.
   * `plain`: no surface and larger tap-sized rows, for bottom sheets.
   */
  variant?: 'bare' | 'column' | 'plain';
  className?: string;
}

export const EntityTreeSidebar: React.FC<EntityTreeSidebarProps> = ({
  mode,
  selection,
  onSelect,
  entityCounts = {},
  totalCount = 0,
  variant = 'bare',
  className = '',
}) => {
  const [search, setSearch] = useState('');
  const [stakeholders, setStakeholders] = useState<EntityNode[]>([]);
  const [organizations, setOrganizations] = useState<EntityNode[]>([]);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['stakeholders', 'organizations']));
  const [loading, setLoading] = useState(true);
  const plain = variant === 'plain';
  const column = variant === 'column';

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      stakeholderService.getStakeholders({ limit: 500, sortBy: 'firstName', sortOrder: 'ASC' }),
      organizationService.getOrganizations({ limit: 500, sortBy: 'name', sortOrder: 'ASC' }),
    ]).then(([sRes, oRes]) => {
      if (cancelled) return;
      setStakeholders(
        (sRes.data || []).map(s => ({
          id: s.id,
          name: `${s.firstName || ''} ${s.lastName || ''}`.trim() || s.professionalEmail || 'Unknown',
          count: entityCounts[`stakeholder:${s.id}`] || 0,
        }))
      );
      setOrganizations(
        (oRes.data || []).map(o => ({
          id: o.id,
          name: o.name || 'Unknown',
          count: entityCounts[`organization:${o.id}`] || 0,
        }))
      );
    }).catch(() => {}).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  // entityCounts changes on every render if we include it, load entities once
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update counts when entityCounts changes (without re-fetching entities)
  const stakeholdersWithCounts = useMemo(() =>
    stakeholders.map(s => ({ ...s, count: entityCounts[`stakeholder:${s.id}`] || 0 })),
    [stakeholders, entityCounts]
  );

  const organizationsWithCounts = useMemo(() =>
    organizations.map(o => ({ ...o, count: entityCounts[`organization:${o.id}`] || 0 })),
    [organizations, entityCounts]
  );

  // Filter entities with items, then apply search
  const filteredStakeholders = useMemo(() => {
    let list = stakeholdersWithCounts.filter(s => s.count > 0);
    if (search) {
      const q = foldText(search);
      list = list.filter(s => s.name.toLowerCase().includes(q));
    }
    return list;
  }, [stakeholdersWithCounts, search]);

  const filteredOrganizations = useMemo(() => {
    let list = organizationsWithCounts.filter(o => o.count > 0);
    if (search) {
      const q = foldText(search);
      list = list.filter(o => o.name.toLowerCase().includes(q));
    }
    return list;
  }, [organizationsWithCounts, search]);

  const stakeholdersTotalCount = useMemo(() =>
    stakeholdersWithCounts.reduce((sum, s) => sum + s.count, 0),
    [stakeholdersWithCounts]
  );
  const organizationsTotalCount = useMemo(() =>
    organizationsWithCounts.reduce((sum, o) => sum + o.count, 0),
    [organizationsWithCounts]
  );
  const unassignedCount = useMemo(() => entityCounts['unassigned'] || 0, [entityCounts]);

  const toggleSection = useCallback((section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  }, []);

  const isActive = (sel: TreeSelection) => sel.type === selection.type && sel.id === selection.id;

  const itemNoun = mode === 'wiki' ? 'pages' : 'files';

  /** `tone`: icon colour for rows of one entity kind; the active row's icon is always red */
  const row = (sel: TreeSelection, label: string, count: number, icon: LucideIcon, activeIcon?: LucideIcon, tone?: string) => {
    const active = isActive(sel);
    const Icon = active && activeIcon ? activeIcon : icon;
    return (
      <button
        key={`${sel.type}:${sel.id ?? ''}`}
        type="button"
        onClick={() => onSelect(sel)}
        aria-current={active ? 'true' : undefined}
        title={label}
        className={`group flex w-full items-center gap-2.5 rounded-lg px-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 ${
          plain ? 'h-10 text-[15px]' : 'h-8 text-[13px]'
        } ${
          active
            ? column
              ? 'bg-white text-ink shadow-[0_1px_2px_rgba(0,0,0,0.05)] ring-1 ring-neutral-200/80'
              : 'bg-neutral-100 text-ink'
            : `text-neutral-600 hover:text-ink ${column ? 'hover:bg-neutral-200/50' : 'hover:bg-neutral-50'}`
        }`}
      >
        <Icon
          className={`h-4 w-4 flex-shrink-0 transition-colors ${
            active ? 'text-primary-600' : tone || 'text-neutral-400 group-hover:text-neutral-500'
          }`}
          strokeWidth={1.75}
          aria-hidden="true"
        />
        <span className="min-w-0 flex-1 truncate">{label}</span>
        <span className={`tabular-nums ${plain ? 'text-[13px]' : 'text-[12px]'} text-neutral-400`}>{count}</span>
      </button>
    );
  };

  const section = (
    key: 'stakeholders' | 'organizations',
    label: string,
    total: number,
    groupIcon: LucideIcon,
    nodes: EntityNode[],
  ) => {
    const open = expandedSections.has(key);
    const single: TreeSelection['type'] = key === 'stakeholders' ? 'stakeholder' : 'organization';
    const tone = ENTITY_TONE[key === 'stakeholders' ? 'stakeholder' : 'organization'].text;
    return (
      <div className="mt-4">
        <button
          type="button"
          onClick={() => toggleSection(key)}
          aria-expanded={open}
          className="flex h-7 w-full items-center gap-1.5 rounded-lg px-2.5 text-[12px] text-neutral-400 transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
        >
          <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? '' : '-rotate-90'}`} strokeWidth={1.75} />
          {label}
        </button>
        {open && (
          <div className="mt-0.5 space-y-0.5">
            {row({ type: key }, `All ${label.toLowerCase()}`, total, groupIcon, undefined, tone)}
            {nodes.map(node => row({ type: single, id: node.id, name: node.name }, node.name, node.count, Folder, FolderOpen, tone))}
            {nodes.length === 0 && (
              <p className="px-2.5 py-1.5 text-[12px] text-neutral-400">{search ? 'No matches' : `No ${itemNoun}`}</p>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={`flex w-full flex-col font-ui ${
        plain ? 'min-h-0' : column ? 'h-full border-r border-neutral-200/70 bg-neutral-50/80' : 'h-full'
      } ${className}`}
    >
      {/* Search */}
      <div className={plain ? 'px-4 pb-2 pt-1' : column ? 'px-3 pb-2 pt-4' : 'p-3 pb-2'}>
        <SearchInput
          placeholder="Filter"
          aria-label="Filter stakeholders and organizations"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* Tree */}
      <nav
        aria-label={mode === 'wiki' ? 'Wiki library' : 'Media library'}
        className={`min-h-0 flex-1 overflow-y-auto overscroll-contain pb-4 [scrollbar-width:thin] ${plain ? 'px-2' : 'px-3'}`}
      >
        <div className="space-y-0.5">
          {row({ type: 'all' }, mode === 'wiki' ? 'All pages' : 'All media', totalCount, mode === 'wiki' ? FileText : Image)}
          {mode === 'media' && !loading && unassignedCount > 0 &&
            row({ type: 'unassigned' }, 'Uncategorized', unassignedCount, Inbox)}
        </div>

        {loading ? (
          <div className="mt-5 space-y-2.5 px-2.5" aria-hidden="true">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-36" />
          </div>
        ) : (
          <>
            {section('stakeholders', 'Stakeholders', stakeholdersTotalCount, Users, filteredStakeholders)}
            {section('organizations', 'Organizations', organizationsTotalCount, Building2, filteredOrganizations)}
          </>
        )}
      </nav>
    </div>
  );
};
